import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import './Dashboard.css';
import ConnectionManager from './ConnectionManager';
import ConnectionsView from './ConnectionsView';
import DbBrowser from './DbBrowser';
import QueryEditor from './QueryEditor';
import ResultsView from './ResultsView';
import MenuBar from './MenuBar';
import Preferences, { getAutocompleteEnabled } from './Preferences';
import QueryHistory from './QueryHistory';
import { connectionsAPI, worksheetAPI, queryAPI, aiAPI } from '../services/api';
import { getErrorMessage } from '../utils/errorMessage';

const Dashboard = ({ user, onLogout }) => {
  const [connections, setConnections] = useState([]);
  const [selectedConnection, setSelectedConnection] = useState(null);
  const [worksheets, setWorksheets] = useState([]);
  const [activeWorksheet, setActiveWorksheet] = useState(null);
  const [activeTab, setActiveTab] = useState('query');
  const [currentView, setCurrentView] = useState('worksheets');
  const [loading, setLoading] = useState(true);
  const [tables, setTables] = useState([]);
  const [tablesPrefixCounts, setTablesPrefixCounts] = useState(null);
  const tablesCacheRef = useRef({}); // Cache tables per connection ID - load only once per connection
  const [packages, setPackages] = useState([]);
  const packagesCacheRef = useRef({}); // Cache packages per connection - load only once, like tables
  const [packagesError, setPackagesError] = useState(null);
  const [packagesLoading, setPackagesLoading] = useState(false);
  const [queryMode, setQueryMode] = useState('report'); // sql | hcm_rest | report
  const [rowLimit, setRowLimit] = useState(500);
  const [worksheetResults, setWorksheetResults] = useState({}); // Store results per worksheet ID
  const [worksheetLoading, setWorksheetLoading] = useState({}); // worksheetId -> true when query running in background
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [showPreferences, setShowPreferences] = useState(false);
  const [autocompleteEnabled, setAutocompleteEnabledState] = useState(() => getAutocompleteEnabled());
  const [editingWorksheetId, setEditingWorksheetId] = useState(null);
  const [editingWorksheetName, setEditingWorksheetName] = useState('');
  const queryEditorRef = useRef(null);
  const fileInputRef = useRef(null);
  const worksheetNameInputRef = useRef(null);
  const connectionManagerRef = useRef(null);

  // Sidebar panel toggles (connections, table browser, db browser)
  const [showConnections, setShowConnections] = useState(true);
  const [showTableBrowser, setShowTableBrowser] = useState(true);
  const [showDbBrowser, setShowDbBrowser] = useState(true);

  // AI chatbot (floating, main page)
  const [showAiPanel, setShowAiPanel] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiGeneratedSql, setAiGeneratedSql] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);

  const handleAiGenerate = async () => {
    const prompt = (aiPrompt || '').trim();
    if (!prompt) {
      toast.info('Describe what data you need (e.g. "list all active workers")');
      return;
    }
    setAiError(null);
    setAiGeneratedSql('');
    setAiLoading(true);
    const tableNames = (tables || []).map(t => typeof t === 'string' ? t : (t && (t.id || t.name)) || '').filter(Boolean);
    try {
      const { data } = await aiAPI.generateSql(prompt, tableNames, 'oracle');
      setAiGeneratedSql(data.sql || '');
    } catch (err) {
      const msg = err.response?.data?.error || getErrorMessage(err);
      const isNotConfigured = err.response?.status === 503 || (err.response?.data?.code === 'AI_NOT_CONFIGURED');
      setAiError(isNotConfigured ? 'AI assistant is not configured. Add OPENAI_API_KEY to backend .env.' : msg);
      if (!isNotConfigured) toast.error(msg);
    } finally {
      setAiLoading(false);
    }
  };

  const handleAiInsertIntoEditor = () => {
    if (!aiGeneratedSql) return;
    if (!activeWorksheet) {
      toast.warning('Open a worksheet first');
      return;
    }
    const current = activeWorksheet.query || '';
    const newQuery = current + (current ? '\n' : '') + aiGeneratedSql;
    updateWorksheet(activeWorksheet.id, { query: newQuery });
    setShowAiPanel(false);
    setAiPrompt('');
    setAiGeneratedSql('');
    setAiError(null);
    toast.success('SQL inserted into current worksheet');
  };

  useEffect(() => {
    loadConnections();
    loadWorksheets();
  }, []);

  useEffect(() => {
    if (editingWorksheetId && worksheetNameInputRef.current) {
      worksheetNameInputRef.current.focus();
      worksheetNameInputRef.current.select();
    }
  }, [editingWorksheetId]);

  // Set connection when active worksheet changes
  useEffect(() => {
    if (activeWorksheet && activeWorksheet.connection_id && connections.length > 0) {
      const conn = connections.find(c => c.id === activeWorksheet.connection_id);
      if (conn) {
        // Only update if it's different to avoid unnecessary re-renders
        if (!selectedConnection || selectedConnection.id !== conn.id) {
          setSelectedConnection(conn);
        }
      } else if (activeWorksheet.connection_id) {
        // Connection ID exists but connection not found - clear selection
        setSelectedConnection(null);
      }
    } else if (activeWorksheet && !activeWorksheet.connection_id && connections.length > 0) {
      // Worksheet has no connection_id - don't change selected connection
      // Keep current selection or select first if none selected
      if (!selectedConnection && connections.length > 0) {
        setSelectedConnection(connections[0]);
      }
    }
  }, [activeWorksheet, connections]);

  // Load tables once per connection (by id). Reuse cache when switching worksheets or same connection.
  const connectionId = selectedConnection?.id ?? null;
  useEffect(() => {
    if (!connectionId) {
      setTables([]);
      return;
    }

    const cache = tablesCacheRef.current;
    if (cache[connectionId]) {
      setTables(cache[connectionId]);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const response = await queryAPI.getTables(connectionId, '');
        if (cancelled) return;
        console.log('Tables API response:', response.data);
        const tableList = response.data.tables || [];
        const prefixCounts = response.data.prefixCounts || null;
        if (response.data.error) {
          console.warn('Tables API error (non-critical, using static TableList):', response.data.error);
          setTables([]);
          setTablesPrefixCounts(null);
          return;
        }
        console.log('Setting tables:', tableList.length, 'tables', prefixCounts ? 'prefixCounts:' : '', prefixCounts);
        cache[connectionId] = tableList;
        setTables(tableList);
        setTablesPrefixCounts(prefixCounts);
      } catch (err) {
        if (!cancelled) {
          console.warn('Failed to load tables (non-critical, using static TableList):', err.message);
          setTables([]);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [connectionId]);

  // Load packages once per connection (same pattern as tables).
  // If GET /api/packages returns 404 (e.g. backend not updated), fallback to query/execute with packages SQL.
  const PACKAGES_SQL = `SELECT DISTINCT name
FROM all_source
WHERE owner = 'FUSION'
AND type = 'PACKAGE'
ORDER BY name`;
  const parsePackagesFromQueryResult = (data) => {
    const rows = data?.data || data?.items || data?.rows || [];
    const cols = data?.columns || [];
    const nameCol = cols[0] || 'NAME';
    return rows.map((row) => {
      if (Array.isArray(row)) return row[0] != null ? String(row[0]).trim() : null;
      const o = row && typeof row === 'object' ? row : {};
      const v = o[nameCol] ?? o.NAME ?? o.name;
      return v != null ? String(v).trim() : null;
    }).filter(Boolean);
  };
  useEffect(() => {
    if (!connectionId) {
      setPackages([]);
      setPackagesError(null);
      setPackagesLoading(false);
      return;
    }
    
    // Check sessionStorage first (persists until logout)
    const sessionCacheKey = `packages_cache_${connectionId}`;
    try {
      const cachedData = sessionStorage.getItem(sessionCacheKey);
      if (cachedData) {
        const parsed = JSON.parse(cachedData);
        if (parsed && Array.isArray(parsed.packages)) {
          console.log('Loading packages from sessionStorage for connection:', connectionId);
          setPackages(parsed.packages);
          setPackagesError(parsed.error || null);
          setPackagesLoading(false);
          // Also update ref cache for consistency
          packagesCacheRef.current[connectionId] = parsed.packages;
          return;
        }
      }
    } catch (e) {
      console.warn('Failed to read packages from sessionStorage:', e);
    }
    
    // Check ref cache as fallback (in-memory only)
    const cache = packagesCacheRef.current;
    if (cache[connectionId]) {
      console.log('Loading packages from memory cache for connection:', connectionId);
      setPackages(cache[connectionId]);
      setPackagesError(null);
      setPackagesLoading(false);
      // Also store in sessionStorage for persistence
      try {
        sessionStorage.setItem(sessionCacheKey, JSON.stringify({ packages: cache[connectionId], error: null }));
      } catch (e) {
        console.warn('Failed to save packages to sessionStorage:', e);
      }
      return;
    }
    
    // Load packages from API (only once per session)
    let cancelled = false;
    setPackagesLoading(true);
    setPackagesError(null);
    (async () => {
      try {
        console.log('Fetching packages for connection:', connectionId);
        const response = await queryAPI.getPackages(connectionId);
        if (cancelled) return;
        const list = response.data.packages || [];
        const error = response.data.error || null;
        console.log('Packages response:', { listLength: list.length, error });
        
        // Store in both ref cache and sessionStorage
        cache[connectionId] = list;
        try {
          sessionStorage.setItem(sessionCacheKey, JSON.stringify({ packages: list, error }));
        } catch (e) {
          console.warn('Failed to save packages to sessionStorage:', e);
        }
        
        setPackages(list);
        setPackagesError(error);
        setPackagesLoading(false);
      } catch (err) {
        if (cancelled) return;
        console.error('Error fetching packages:', err);
        console.error('Error details:', {
          status: err?.response?.status,
          statusText: err?.response?.statusText,
          data: err?.response?.data,
          message: err?.message
        });
        
        // Try fallback for 404 or 500 errors
        if (err?.response?.status === 404 || err?.response?.status === 500) {
          try {
            console.log('Packages endpoint failed, trying fallback SQL query...');
            const res = await queryAPI.execute(connectionId, PACKAGES_SQL, 'rest', 'sql');
            if (cancelled) return;
            const data = res.data;
            if (data?.success && data?.data) {
              const list = parsePackagesFromQueryResult(data);
              console.log('Fallback query returned packages:', list.length);
              
              // Store in both ref cache and sessionStorage
              cache[connectionId] = list;
              try {
                sessionStorage.setItem(sessionCacheKey, JSON.stringify({ packages: list, error: null }));
              } catch (e) {
                console.warn('Failed to save packages to sessionStorage:', e);
              }
              
              setPackages(list);
              setPackagesError(null);
            } else {
              setPackages([]);
              setPackagesError(data?.error || 'Failed to load packages');
            }
            setPackagesLoading(false);
          } catch (fallbackErr) {
            if (!cancelled) {
              console.error('Fallback query error:', fallbackErr);
              const errorMsg = fallbackErr?.response?.data?.error || fallbackErr?.response?.data?.message || fallbackErr?.message || 'Failed to load packages';
              setPackages([]);
              setPackagesError(errorMsg);
              setPackagesLoading(false);
            }
          }
        } else {
          const errorMsg = err?.response?.data?.error || err?.response?.data?.message || err?.message || 'Failed to load packages';
          setPackages([]);
          setPackagesError(errorMsg);
          setPackagesLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [connectionId]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ctrl+S or Cmd+S - Save
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        handleSave();
      }
      // Ctrl+Shift+S or Cmd+Shift+S - Save As
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'S') {
        e.preventDefault();
        handleSaveAs();
      }
      // Ctrl+O or Cmd+O - Open
      if ((e.ctrlKey || e.metaKey) && e.key === 'o') {
        e.preventDefault();
        handleOpen();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [activeWorksheet]);

  const loadConnections = async () => {
    try {
      const response = await connectionsAPI.getAll();
      // Backend returns 'connection' (singular array) to match OJET frontend
      const connectionsList = response.data.connection || response.data.connections || [];
      setConnections(connectionsList);
      
      // Auto-select first connection if available
      if (connectionsList.length > 0 && !selectedConnection) {
        setSelectedConnection(connectionsList[0]);
      }
      return connectionsList;
    } catch (error) {
      console.error('Error loading connections:', error);
      let errorMessage = 'Failed to load connections';
      
      if (error.code === 'ERR_NETWORK' || error.message?.includes('Network Error')) {
        errorMessage = 'Network error: Cannot connect to backend server. Please ensure the backend is running on http://localhost:5000';
      } else if (error.response?.status === 401) {
        errorMessage = 'Authentication expired. Please log in again.';
      } else if (error.response?.data?.error) {
        errorMessage = `Error: ${error.response.data.error}`;
      } else if (error.message) {
        errorMessage = `Error: ${error.message}`;
      }
      
      toast.error(errorMessage);
      setConnections([]); // Ensure connections is always an array
      return [];
    } finally {
      setLoading(false);
    }
  };

  const loadWorksheets = async () => {
    try {
      const response = await worksheetAPI.getAll();
      const worksheetList = response.data.worksheets || [];
      // Keep queries from database - don't clear them
      // Queries persist until logout
      
      // Always ensure Worksheet1 exists
      let worksheet1 = worksheetList.find(ws => ws.name === 'Worksheet1' || ws.name === 'Worksheet 1');
      
      if (!worksheet1) {
        // Create Worksheet1 if it doesn't exist
        try {
          const createResponse = await worksheetAPI.create({
            name: 'Worksheet1',
            connection_id: null,
            query: ''
          });
          worksheet1 = createResponse.data.worksheet;
          // Add Worksheet1 to the list
          worksheetList.unshift(worksheet1);
        } catch (createError) {
          console.error('Failed to create Worksheet1:', createError);
          // If creation fails, use first worksheet or create a default one
          if (worksheetList.length > 0) {
            worksheet1 = worksheetList[0];
          } else {
            await createWorksheet('Worksheet1');
            return; // createWorksheet will set activeWorksheet
          }
        }
      } else {
        // Rename if it's "Worksheet 1" to "Worksheet1"
        if (worksheet1.name === 'Worksheet 1') {
          try {
            await worksheetAPI.update(worksheet1.id, { name: 'Worksheet1' });
            worksheet1.name = 'Worksheet1';
          } catch (updateError) {
            console.error('Failed to rename worksheet:', updateError);
          }
        }
      }

      // Show all worksheets (sorted by position)
      const sorted = [...worksheetList].sort((a, b) => (a.position || 0) - (b.position || 0));
      setWorksheets(sorted);
      setActiveWorksheet(worksheet1);
    } catch (error) {
      toast.error('Failed to load worksheets');
      // Create Worksheet1 as fallback
      try {
        await createWorksheet('Worksheet1');
      } catch (createError) {
        console.error('Failed to create fallback Worksheet1:', createError);
      }
    }
  };

  const createWorksheet = async (name) => {
    try {
      const defaultName = name || (worksheets.length === 0 ? 'Worksheet1' : `Worksheet ${worksheets.length + 1}`);
      const response = await worksheetAPI.create({
        name: defaultName,
        connection_id: selectedConnection?.id,
        query: ''
      });
      const newWorksheet = response.data.worksheet;
      const updatedWorksheets = [...worksheets, newWorksheet];
      setWorksheets(updatedWorksheets);
      setActiveWorksheet(newWorksheet);
      if (name !== 'Worksheet1') {
        toast.success('Worksheet created');
      }
    } catch (error) {
      const msg = error.response?.data?.error || 'Failed to create worksheet';
      toast.error(msg);
    }
  };

  const updateWorksheet = async (worksheetId, updates) => {
    try {
      await worksheetAPI.update(worksheetId, updates);
      const updatedWorksheets = worksheets.map(ws => 
        ws.id === worksheetId ? { ...ws, ...updates } : ws
      );
      setWorksheets(updatedWorksheets);
      // Also update activeWorksheet if it's the one being updated
      if (activeWorksheet?.id === worksheetId) {
        setActiveWorksheet({ ...activeWorksheet, ...updates });
      }
    } catch (error) {
      console.error('Failed to update worksheet:', error);
    }
  };

  const startRenameWorksheet = (worksheet) => {
    setEditingWorksheetId(worksheet.id);
    setEditingWorksheetName(worksheet.name || '');
  };

  const finishRenameWorksheet = (worksheetId) => {
    const trimmed = (editingWorksheetName || '').trim();
    if (trimmed) {
      updateWorksheet(worksheetId, { name: trimmed });
    }
    setEditingWorksheetId(null);
    setEditingWorksheetName('');
  };

  const deleteWorksheet = async (worksheetId) => {
    if (worksheets.length === 1) {
      toast.warning('Cannot delete the last worksheet');
      return;
    }

    if (!window.confirm('Delete this worksheet?')) {
      return;
    }

    try {
      await worksheetAPI.delete(worksheetId);
      const newWorksheets = worksheets.filter(ws => ws.id !== worksheetId);
      setWorksheets(newWorksheets);
      setWorksheetLoading(prev => {
        const next = { ...prev };
        delete next[worksheetId];
        return next;
      });
      
      if (activeWorksheet?.id === worksheetId) {
        setActiveWorksheet(newWorksheets[0]);
      }
      toast.success('Worksheet deleted');
    } catch (error) {
      toast.error('Failed to delete worksheet');
    }
  };

  const handleConnectionChange = (connection) => {
    setSelectedConnection(connection);
    // Update active worksheet connection
    if (activeWorksheet) {
      updateWorksheet(activeWorksheet.id, { connection_id: connection?.id });
    }
  };

  // Menu handlers
  const handleSave = async () => {
    if (!activeWorksheet) {
      toast.warning('No active worksheet to save');
      return;
    }

    const content = queryEditorRef.current?.getContent?.();
    const queryText = content?.full || activeWorksheet.query || '';
    
    if (!queryText.trim()) {
      toast.warning('No content to save');
      return;
    }

    const fileName = `${activeWorksheet.name.replace(/[^a-z0-9]/gi, '_')}.sql`;
    
    // Try File System Access API (modern browsers)
    if ('showSaveFilePicker' in window) {
      try {
        const fileHandle = await window.showSaveFilePicker({
          suggestedName: fileName,
          types: [{
            description: 'SQL Files',
            accept: { 'text/plain': ['.sql'] }
          }]
        });
        
        const writable = await fileHandle.createWritable();
        await writable.write(queryText);
        await writable.close();
        
        toast.success(`File saved: ${fileHandle.name}`);
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Error saving file:', err);
          toast.error('Failed to save file');
        }
      }
    } else {
      // Fallback: Download file
      const blob = new Blob([queryText], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`File downloaded: ${fileName}`);
    }
  };

  const handleSaveAs = async () => {
    if (!activeWorksheet) {
      toast.warning('No active worksheet to save');
      return;
    }

    const content = queryEditorRef.current?.getContent?.();
    const queryText = content?.full || activeWorksheet.query || '';
    
    if (!queryText.trim()) {
      toast.warning('No content to save');
      return;
    }

    const defaultName = `${activeWorksheet.name.replace(/[^a-z0-9]/gi, '_')}_copy.sql`;
    
    // Try File System Access API (modern browsers)
    if ('showSaveFilePicker' in window) {
      try {
        const fileHandle = await window.showSaveFilePicker({
          suggestedName: defaultName,
          types: [{
            description: 'SQL Files',
            accept: { 'text/plain': ['.sql'] }
          }]
        });
        
        const writable = await fileHandle.createWritable();
        await writable.write(queryText);
        await writable.close();
        
        toast.success(`File saved: ${fileHandle.name}`);
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Error saving file:', err);
          toast.error('Failed to save file');
        }
      }
    } else {
      // Fallback: Prompt for filename then download
      const fileName = prompt('Enter filename:', defaultName) || defaultName;
      const blob = new Blob([queryText], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName.endsWith('.sql') ? fileName : `${fileName}.sql`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`File downloaded: ${a.download}`);
    }
  };

  const handleOpen = () => {
    // Trigger file input click
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const text = await file.text();
      
      // Create a new worksheet with the file content
      const fileName = file.name.replace(/\.sql$/i, '');
      const newWorksheet = await worksheetAPI.create({
        name: fileName,
        connection_id: selectedConnection?.id || null,
        query: text
      });
      
      const createdWorksheet = newWorksheet.data.worksheet;
      await loadWorksheets();
      setActiveWorksheet(createdWorksheet);
      setCurrentView('worksheets');
      toast.success(`Opened file: ${file.name}`);
    } catch (error) {
      console.error('Error opening file:', error);
      toast.error('Failed to open file');
    }
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleViewHistory = () => {
    setCurrentView('history');
    toast.info('Showing query history');
  };

  const handleViewConnections = () => {
    setCurrentView('connections');
    if (!selectedConnection && connections.length > 0) {
      setSelectedConnection(connections[0]);
    }
  };

  const handleDeleteConnection = async (connectionId) => {
    const wasSelected = selectedConnection?.id === connectionId;
    await connectionsAPI.delete(connectionId);
    const list = await loadConnections();
    if (wasSelected) {
      setSelectedConnection(list.length > 0 ? list[0] : null);
    }
  };

  const handleViewWorksheets = () => {
    setCurrentView('worksheets');
  };

  // Edit menu handlers
  const handleUndo = () => {
    if (queryEditorRef.current && queryEditorRef.current.undo) {
      queryEditorRef.current.undo();
    } else {
      toast.info('Undo not available');
    }
  };

  const handleRedo = () => {
    if (queryEditorRef.current && queryEditorRef.current.redo) {
      queryEditorRef.current.redo();
    } else {
      toast.info('Redo not available');
    }
  };

  const handleCut = async () => {
    if (queryEditorRef.current) {
      const content = queryEditorRef.current.getContent();
      const selected = content?.selected || '';
      if (!selected) {
        toast.info('No text selected');
        return;
      }
      try {
        await navigator.clipboard.writeText(selected);
        if (queryEditorRef.current.cut) {
          queryEditorRef.current.cut();
        } else {
          toast.info('Cut operation not available');
        }
      } catch (err) {
        toast.error('Failed to cut text');
      }
    }
  };

  const handleCopy = async () => {
    if (queryEditorRef.current) {
      const content = queryEditorRef.current.getContent();
      const selected = content?.selected || '';
      if (!selected) {
        toast.info('No text selected');
        return;
      }
      try {
        await navigator.clipboard.writeText(selected);
        toast.success('Copied to clipboard');
      } catch (err) {
        toast.error('Failed to copy text');
      }
    }
  };

  const handlePaste = async () => {
    if (queryEditorRef.current) {
      try {
        const text = await navigator.clipboard.readText();
        if (queryEditorRef.current.paste) {
          queryEditorRef.current.paste(text);
        } else {
          toast.info('Paste operation not available');
        }
      } catch (err) {
        toast.error('Failed to paste text');
      }
    }
  };

  const handleLoadQueryFromHistory = (query) => {
    if (activeWorksheet) {
      updateWorksheet(activeWorksheet.id, { query });
      setCurrentView('worksheets');
    } else {
      toast.warning('Please create a worksheet first');
    }
  };

  const handleResultsChange = (worksheetId, results, connectionName) => {
    if (worksheetId == null) return;
    setWorksheetResults(prev => ({
      ...prev,
      [worksheetId]: results == null ? null : {
        ...results,
        connectionName: connectionName
      }
    }));
  };

  const handleExecutionStart = (worksheetId) => {
    if (worksheetId) setWorksheetLoading(prev => ({ ...prev, [worksheetId]: true }));
  };

  const handleExecutionEnd = (worksheetId) => {
    if (worksheetId) setWorksheetLoading(prev => ({ ...prev, [worksheetId]: false }));
  };

  return (
    <div className="dashboard">
      <header className="dashboard-header">
        <div className="header-left">
          <div className="logo-container">
            <img src="/cloudfish-logo.png" alt="CloudFish Logo" className="logo-image logo-cloudfish" />
          </div>
          <MenuBar
            onSave={handleSave}
            onSaveAs={handleSaveAs}
            onOpen={handleOpen}
            onUndo={handleUndo}
            onRedo={handleRedo}
            onCut={handleCut}
            onCopy={handleCopy}
            onPaste={handlePaste}
            onPreferences={() => setShowPreferences(true)}
            onViewHistory={handleViewHistory}
            onViewConnections={handleViewConnections}
            onViewWorksheets={handleViewWorksheets}
            currentView={currentView}
          />
        </div>
        <div className="header-right">
          {selectedConnection && (
            <span className="connection-badge-worksheet" title={`Connected to: ${selectedConnection.name}`}>
              Connected to: {selectedConnection.name}
            </span>
          )}
          {!selectedConnection && connections.length > 0 && (
            <span className="connection-badge-worksheet no-connection" title="No connection selected">
              No Connection
            </span>
          )}
          <div className="header-panel-toggles" role="toolbar" aria-label="Panel toggles">
            <button
              type="button"
              className={`sidebar-icon-btn ${showConnections ? 'active' : ''}`}
              onClick={() => setShowConnections(s => !s)}
              title={showConnections ? 'Hide connections' : 'Show connections'}
              aria-label={showConnections ? 'Hide connections' : 'Show connections'}
              aria-pressed={showConnections}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M18 20V10" /><path d="M12 20V4" /><path d="M6 20v-6" />
              </svg>
              <span className="sidebar-icon-label">Conn</span>
            </button>
            <button
              type="button"
              className={`sidebar-icon-btn ${showTableBrowser ? 'active' : ''}`}
              onClick={() => setShowTableBrowser(s => !s)}
              title={showTableBrowser ? 'Hide table browser' : 'Show table browser'}
              aria-label={showTableBrowser ? 'Hide table browser' : 'Show table browser'}
              aria-pressed={showTableBrowser}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <rect x="3" y="3" width="18" height="18" rx="2" /><path d="M3 9h18" /><path d="M3 15h18" /><path d="M9 3v18" />
              </svg>
              <span className="sidebar-icon-label">Tables</span>
            </button>
            <button
              type="button"
              className={`sidebar-icon-btn ${showDbBrowser ? 'active' : ''}`}
              onClick={() => setShowDbBrowser(s => !s)}
              title={showDbBrowser ? 'Hide DB browser' : 'Show DB browser'}
              aria-label={showDbBrowser ? 'Hide DB browser' : 'Show DB browser'}
              aria-pressed={showDbBrowser}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <ellipse cx="12" cy="5" rx="9" ry="3" /><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" /><path d="M3 12c0 1.66 4 3 9 3s9-1.34 9-3" />
              </svg>
              <span className="sidebar-icon-label">DB</span>
            </button>
          </div>
          <button onClick={onLogout} className="btn-logout">
            Logout
          </button>
        </div>
      </header>

      <div className="dashboard-content">
        {(showConnections || showDbBrowser) && (
        <aside className="sidebar">
          {showConnections && (
          <div className="sidebar-section sidebar-section-connections">
            <ConnectionManager
              ref={connectionManagerRef}
              connections={connections}
              selectedConnection={selectedConnection}
              onConnectionChange={handleConnectionChange}
              onConnectionsUpdate={loadConnections}
              onDelete={handleDeleteConnection}
            />
          </div>
          )}
          {showDbBrowser && (
          <div className="sidebar-section sidebar-section-db">
            <DbBrowser
              selectedConnection={selectedConnection}
              packages={packages}
              packagesError={packagesError}
              packagesLoading={packagesLoading}
              onPackageSelect={(name) => setSelectedPackage(name)}
            />
          </div>
          )}
        </aside>
        )}

        <main className="main-content">
          {currentView === 'connections' && (
            <ConnectionsView
              connections={connections}
              selectedConnection={selectedConnection}
              onConnectionChange={handleConnectionChange}
              onEdit={(conn) => connectionManagerRef.current?.openEdit(conn)}
              onDelete={handleDeleteConnection}
              onAddConnection={() => connectionManagerRef.current?.openAdd()}
            />
          )}

          {currentView === 'history' && (
            <QueryHistory
              key="query-history"
              onLoadQuery={handleLoadQueryFromHistory}
              visible={true}
            />
          )}

          {currentView === 'worksheets' && (
            <>
              <div className="worksheet-tabs">
                <div className="worksheet-tabs-container">
                  <div className="worksheet-tabs-list">
                    {worksheets.map((worksheet) => (
                      <div
                        key={worksheet.id}
                        className={`worksheet-tab ${activeWorksheet?.id === worksheet.id ? 'active' : ''}`}
                        onClick={() => {
                          if (editingWorksheetId !== worksheet.id) setActiveWorksheet(worksheet);
                        }}
                      >
                        {editingWorksheetId === worksheet.id ? (
                          <input
                            ref={worksheetNameInputRef}
                            type="text"
                            className="worksheet-name-input"
                            value={editingWorksheetName}
                            onChange={(e) => setEditingWorksheetName(e.target.value)}
                            onBlur={() => finishRenameWorksheet(worksheet.id)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                finishRenameWorksheet(worksheet.id);
                              } else if (e.key === 'Escape') {
                                setEditingWorksheetId(null);
                                setEditingWorksheetName('');
                                worksheetNameInputRef.current?.blur();
                              }
                            }}
                            onClick={(e) => e.stopPropagation()}
                            title="Enter new name, press Enter to save"
                          />
                        ) : (
                          <span
                            className="worksheet-name"
                            onDoubleClick={(e) => {
                              e.stopPropagation();
                              startRenameWorksheet(worksheet);
                            }}
                            title="Double-click to rename"
                          >
                            {worksheet.name}
                            {worksheetLoading[worksheet.id] && (
                              <span className="worksheet-tab-loading" title="Query running in background"> …</span>
                            )}
                          </span>
                        )}
                        {worksheets.length > 1 && (
                          <button
                            className="btn-close-worksheet"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (editingWorksheetId === worksheet.id) setEditingWorksheetId(null);
                              deleteWorksheet(worksheet.id);
                            }}
                            title="Close worksheet"
                          >
                            ✕
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      className="btn-new-worksheet"
                      onClick={() => createWorksheet()}
                      title="New worksheet"
                    >
                      +
                    </button>
                  </div>
                  <div className="worksheet-tabs-controls">
                    <div className="mode-selector-worksheet" title="SQL: run SQL via ORDS. HCM REST: call Oracle Fusion HCM REST APIs. Report: run a report. REST: Postman-style arbitrary API calls.">
                      <label>Query type:</label>
                      <select value={queryMode} onChange={(e) => setQueryMode(e.target.value)}>
                        <option value="sql">SQL (ORDS / custom endpoint)</option>
                        <option value="hcm_rest" title="Call Oracle Fusion HCM REST APIs. Enter a resource path, e.g. emps or emps?limit=100">HCM REST resource</option>
                        <option value="report">Run report</option>
                        <option value="postman">REST (Postman)</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              <div className="tab-content">
                {activeWorksheet && (
                  <QueryEditor
                    ref={queryEditorRef}
                    key={activeWorksheet.id}
                    worksheet={activeWorksheet}
                    selectedConnection={selectedConnection}
                    tables={tables}
                    tablesPrefixCounts={tablesPrefixCounts}
                    packages={packages}
                    queryMode={queryMode}
                    rowLimit={rowLimit}
                    autocompleteEnabled={autocompleteEnabled}
                    initialResults={worksheetResults[activeWorksheet.id]}
                    selectedPackage={selectedPackage}
                    showTableBrowser={showTableBrowser}
                    onExecutionStart={handleExecutionStart}
                    onExecutionEnd={handleExecutionEnd}
                    initialLoading={!!worksheetLoading[activeWorksheet.id]}
                    onRowLimitChange={(newLimit) => setRowLimit(newLimit)}
                    onResultsChange={(worksheetId, results, connectionName) => handleResultsChange(worksheetId, results, connectionName)}
                    onNoConnection={() => toast.warning('Please create a connection first')}
                    onQueryChange={(query) => updateWorksheet(activeWorksheet.id, { query })}
                  />
                )}
              </div>
            </>
          )}
          {/* Hidden file input for Open dialog - always in DOM so Open works on any page */}
          <input
            ref={fileInputRef}
            type="file"
            accept=".sql,.txt"
            style={{ display: 'none' }}
            onChange={handleFileSelect}
          />
        </main>
      </div>

      {/* Floating AI chatbot button - Gold-Yearly plan only */}
      {user?.plan === 'gold_yearly' && (
        <>
          <div className="ai-sidekick-container">
            <button
              type="button"
              className="ai-chat-fab"
              onClick={() => { setShowAiPanel(!showAiPanel); if (showAiPanel) { setAiError(null); setAiGeneratedSql(''); } }}
              title="AI Sidekick"
              aria-label="Open AI Sidekick"
            >
              <img src="/ai-sidekick.png" alt="AI Sidekick" className="ai-sidekick-icon" />
            </button>
            <span className="ai-sidekick-label">AI Sidekick</span>
          </div>

          {/* AI assistant panel (slide-out from bottom right) */}
          {showAiPanel && (
        <div className="ai-chat-panel">
          <div className="ai-chat-panel-header">
            <div className="ai-chat-panel-title">
              <img src="/ai-sidekick.png" alt="AI Sidekick" className="ai-sidekick-header-icon" />
              <h3>AI Sidekick</h3>
            </div>
            <button type="button" className="ai-chat-panel-close" onClick={() => setShowAiPanel(false)} aria-label="Close">✕</button>
          </div>
          <p className="ai-chat-panel-hint">Describe what you want to query (e.g. &quot;list all active workers&quot;, &quot;count employees by department&quot;).</p>
          <textarea
            className="ai-chat-panel-prompt"
            placeholder="e.g. Select first 10 workers with status Active"
            value={aiPrompt}
            onChange={e => setAiPrompt(e.target.value)}
            rows={3}
            disabled={aiLoading}
          />
          <div className="ai-chat-panel-actions">
            <button type="button" className="ai-chat-btn-generate" onClick={handleAiGenerate} disabled={aiLoading}>
              {aiLoading ? 'Generating...' : 'Generate SQL'}
            </button>
          </div>
          {aiError && <div className="ai-chat-panel-error">{aiError}</div>}
          {aiGeneratedSql && (
            <div className="ai-chat-panel-result">
              <label>Generated SQL</label>
              <pre className="ai-chat-generated-sql">{aiGeneratedSql}</pre>
              <button type="button" className="ai-chat-btn-insert" onClick={handleAiInsertIntoEditor}>
                Insert into editor
              </button>
            </div>
          )}
        </div>
          )}
        </>
      )}

      {/* Preferences modal */}
      <Preferences
        isOpen={showPreferences}
        onClose={() => setShowPreferences(false)}
        autocompleteEnabled={autocompleteEnabled}
        onAutocompleteChange={setAutocompleteEnabledState}
      />
    </div>
  );
};

export default Dashboard;

